import { useState } from "react"
import { toast } from "sonner"
import { Shift, DayGroup } from "@/types"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/contexts/auth"
;
import { 
  getDefaultDayGroup, 
  dayTimeDeductionToDayGroups, 
  buildDayTimeDeduction,
  validateDayGroups
} from '@/utils/shiftFormUtils';

export function useShiftForm(
  shift: Shift | null,
  onSave: (shift: Shift, isNew: boolean) => void
) {
  const [category, setCategory] = useState(shift?.name || "")
  const [shiftName, setShiftName] = useState(shift?.code || "")
  const [validFrom, setValidFrom] = useState(shift?.valid_from || "")
  const [inWorkingHours, setInWorkingHours] = useState(shift?.in_working_hours ?? false)
  const [dayGroups, setDayGroups] = useState<DayGroup[]>(() => {
    if (shift?.day_time_deduction && shift.day_time_deduction.length > 0) {
      return dayTimeDeductionToDayGroups(shift.day_time_deduction);
    }
    return [getDefaultDayGroup()];
  })
  const [isSaving, setIsSaving] = useState(false)
  const { user } = useAuth()
  
  // Save the current shift using immutable versioning
  const saveShift = async () => {
    try {
      if (!user?.companyId) {
        toast.error("Cannot save shift: No company ID found")
        return
      }

      // Validate form data
      if (!category.trim()) {
        toast.error("Category is required")
        return
      }
      
      if (!shiftName.trim()) {
        toast.error("Shift name is required")
        return
      }

      // Validate day groups
      const validation = validateDayGroups(dayGroups)
      if (!validation.isValid) {
        toast.error(validation.errors[0])
        return
      }
      
      
      setIsSaving(true)
      
      // Use upsert_shift RPC for immutable versioning
      await upsertShift()
    } catch {
      toast.error("An error occurred while saving the shift")
    } finally {
      setIsSaving(false)
    }
  }

  const upsertShift = async () => {
    try {
      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0]
      
      // Only validate that valid_from is not before today for NEW shifts
      // For existing shifts (updates), allow past dates since the shift was created earlier
      if (!shift?.id && validFrom && validFrom < today) {
        toast.error("Valid From date cannot be before today")
        return
      }
      
      // Build day_time_deduction array from day groups
      let day_time_deduction;
      try {
        day_time_deduction = buildDayTimeDeduction(dayGroups);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Invalid day configuration");
        return;
      }
      
      // Prepare the parameters for upsert_shift RPC
      const params = {
        p_company_id: user!.companyId,
        p_name: category, // Category maps to name
        p_code: shiftName, // Shift Name maps to code
        p_valid_from: validFrom || today, // Default to today if not provided
        p_day_time_deduction: day_time_deduction,
        p_in_working_hours: inWorkingHours, // NEW: Overtime hours policy
        p_replaces_id: shift?.id || null, // Only for edit mode
        p_created_by: user?.id || null, // Track who created/updated
        p_updated_by: user?.id || null // Track who created/updated
      }

      

      const { data, error } = await supabase.rpc('upsert_shift', params)
      
      if (error) {
        
        
        // Map database trigger errors to user-friendly messages
        let errorMessage = "Failed to save shift";
        if (error.message?.includes('day_time_deduction')) {
          errorMessage = "Invalid day/time configuration. Please check your schedule.";
        } else if (error.message?.includes('duplicate')) {
          errorMessage = "A day cannot be repeated across time blocks.";
        } else if (error.message?.includes('time format')) {
          errorMessage = "Invalid time format. Please use HH:MM format.";
        }
        
        toast.error(errorMessage)
        return
      }
      
      if (data) {
        // Convert the returned shift data to our Shift interface
        const savedShift: Shift = {
          id: data.id,
          name: data.name,
          code: data.code,
          valid_from: data.valid_from,
          valid_to: data.valid_to,
          day_time_deduction: data.day_time_deduction || [],
          is_active: data.is_active,
          is_del: data.is_del || false,
          created_by: data.created_by,
          created_at: data.created_at,
          updated_by: data.updated_by,
          updated_at: data.updated_at,
          company_id: data.company_id,
          in_working_hours: data.in_working_hours
        }
        
        const isNew = !shift // If no shift was provided, it's a new shift
        onSave(savedShift, isNew)
        
        const message = isNew ? "New shift added successfully" : "Shift updated successfully"
        toast.success(message)
        
        
      }
      } catch {
      toast.error("Failed to save shift")
    }
  }
  
  return {
    category,
    setCategory,
    shiftName,
    setShiftName,
    validFrom,
    setValidFrom,
    inWorkingHours,
    setInWorkingHours,
    dayGroups,
    setDayGroups,
    isSaving,
    saveShift
  }
}
